import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, EyeIcon, ClockIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import { retailSaleApi, RetailSale } from '../../utils/retailSalesApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { getErrorMessage } from '../../utils/api';

const LiveSalesMonitor: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const [draftSales, setDraftSales] = useState<RetailSale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  useEffect(() => {
    if (!selectedLocation?.id) return;

    const loadDraftSales = async () => {
      try {
        setError('');
        const sales = await retailSaleApi.getLiveDrafts(selectedLocation.id);
        setDraftSales(sales);
        setLastUpdate(new Date());
      } catch (err: any) {
        setError(getErrorMessage(err, 'Failed to load live sales'));
      } finally {
        setLoading(false);
      }
    };

    // Load immediately
    loadDraftSales();

    // Refresh every 10 seconds for real-time monitoring
    const interval = setInterval(loadDraftSales, 10000);

    return () => clearInterval(interval);
  }, [selectedLocation?.id]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);

    if (diffMins < 1) {
      return `${diffSecs} seconds ago`;
    } else if (diffMins === 1) {
      return '1 minute ago';
    } else {
      return `${diffMins} minutes ago`;
    }
  };

  // Admin-only access check
  if (user?.role !== 'admin') {
    return (
      <div className="container-mobile py-8 text-center">
        <ShieldCheckIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-600 mb-4">This page requires admin privileges.</p>
        <Link 
          to="/retail-sales/list" 
          className="inline-block text-blue-600 hover:text-blue-800 underline"
        >
          View Sales List
        </Link>
      </div>
    );
  }

  if (businessType !== 'fish') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for fish farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/retail-sales" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Live Sales Monitor</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <div className="text-sm text-gray-500 flex items-center space-x-2">
          <ClockIcon className="h-4 w-4" />
          <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && draftSales.length === 0 ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : draftSales.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-gray-600 text-lg">No active sales at the moment</p>
          <p className="text-gray-500 text-sm mt-2">
            Sales being filled out at the farm will appear here in real-time
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 font-medium">
              {draftSales.length} active sale{draftSales.length !== 1 ? 's' : ''} happening right now
            </p>
          </div>

          {draftSales.map((sale) => (
            <div key={sale.id} className="card p-6 space-y-4 border-2 border-green-200 bg-green-50">
              {/* Sale Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {sale.sale_type === 'on_farm' ? 'On-Farm Sale' : 'Market Sale'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    Seller: <span className="font-medium">{sale.seller_name || 'Unknown'}</span>
                    {sale.seller_mobile && (
                      <span className="ml-2">({sale.seller_mobile})</span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                    LIVE
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Updated {getTimeAgo(sale.updated_at)}
                  </p>
                </div>
              </div>

              {/* Sale Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Sale Date</p>
                  <p className="font-medium">{new Date(sale.sale_date).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Sale Number</p>
                  <p className="font-medium">{sale.retail_sale_number}</p>
                </div>
                {sale.pond_names && sale.pond_names.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-600">Ponds</p>
                    <p className="font-medium">{sale.pond_names.join(', ')}</p>
                  </div>
                )}
              </div>

              {/* On-Farm Sale Details */}
              {sale.sale_type === 'on_farm' && sale.cartons && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Fish Crates</h4>
                  <div className="space-y-2">
                    {sale.cartons.map((carton, index) => (
                      <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium text-gray-900">
                              Crate #{carton.carton_number || index + 1}
                            </span>
                            <span className="ml-2 text-sm text-gray-600">
                              {carton.species} - {carton.quantity_kg} kg
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900">
                              ₹{carton.amount.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-500">
                              @ ₹{carton.rate_per_kg}/kg
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                    <div>
                      <p className="text-sm text-gray-600">Total Amount</p>
                      <p className="text-lg font-bold text-blue-600">
                        ₹{sale.total_amount?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Amount Received</p>
                      <p className="text-lg font-medium text-gray-900">
                        ₹{sale.amount_received?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Amount Due</p>
                      <p className={`text-lg font-bold ${(sale.amount_due || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        ₹{sale.amount_due?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Market Sale Details */}
              {sale.sale_type === 'market' && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Sold Quantity</p>
                      <p className="font-medium">{sale.sold_quantity_kg} kg</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Returned Quantity</p>
                      <p className="font-medium">{sale.returned_quantity_kg} kg</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Rate</p>
                      <p className="font-medium">₹{sale.rate_per_kg}/kg</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Net Amount</p>
                      <p className="font-bold text-blue-600">₹{sale.net_amount?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* View Details Link */}
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <Link
                  to={`/retail-sales/${sale.id}`}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <EyeIcon className="h-5 w-5" />
                  <span>View Details</span>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveSalesMonitor;

